import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, of, tap, map, startWith, take, Subscription, distinctUntilChanged, forkJoin, catchError, throttleTime, Subject, debounceTime, filter, switchMap, takeUntil } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Logger, ConfirmDialogComponent, OrganizationService, PreviewMode, UtilityService, } from 'src/app/@shared';
import { Offer, OfferDomain, OfferService, OfferTag, OfferPromoService, OfferPromo, PromoDomain, EventService, Event, OfferGroupDomain, ProductCategory, ProductCategoryService, MontageBuilderService, SettingsService, OfferDetailService, OfferPricingFormComponent } from '../..';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import { Status } from '../../models/offer-promo-status';
import * as dayjs from 'dayjs';
import { AuthorizeControlService, CommentsComponent } from 'pr1-ui-components';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { WorkflowStage } from '../../models/workflow-stage.model';
import { ValidatorsService } from 'src/app/@shared/services/validators.service';
import { IPrintPage } from '../../models/print-page.model';
import { CustomFieldDefinition, CustomFieldType } from '../../models/custom-field.model';
import { CustomFieldService } from '../../services/custom-field.service';
import {
  EditUndoController,
  sortOfferEditUndoChanges,
} from '../../services/edit-undo.controller';

const log = new Logger('OfferGroupDetailsComponent');

@Component({
  selector: 'app-offer-group-details',
  templateUrl: './offer-group-details.component.html',
  styleUrls: ['./offer-group-details.component.scss'],
})
export class OfferGroupDetailsComponent<T extends Offer, TOfferDomain extends OfferDomain<Offer>, TOfferPromo extends OfferPromo, TEvent extends Event, TPromoDomain extends PromoDomain<OfferPromo>, TProductCategory extends ProductCategory,> implements OnInit, OnDestroy {
  @ViewChild('pricingForm') pricingForm?: OfferPricingFormComponent;
  offer$: Observable<OfferGroupDomain<Offer>> = of({} as OfferGroupDomain<Offer>);
  customFields$: Observable<CustomFieldDefinition[]> = of([]);
  customFieldValues: any = {};
  assetsUrl$ = this.organizationService.assetsUrl$;
  assetsforBadge: any;
  offertypes: any;
  countries: string[] = [];
  currencies: string[] = [];
  languages: string[] = [];
  noneValue = null;
  private prevHeadline = '';
  private prevOfferBodyCopy = '';
  public showJunctionAi?: boolean = false;
  tagCtrl = new FormControl<string>('', [Validators.minLength(1), Validators.maxLength(50)]);
  offerTagIds: string[] = [];
  updatedOfferTagIds: string[] = [];
  searchCount: number = 0;
  pricingDetails: any = {};
  isPricingFormValid: boolean = true;
  private destroy$ = new Subject<void>();
  private exitSaveResult$ = new Subject<boolean>();
  private savingForExit = false;
  workflowStages: WorkflowStage[] = []
  printPagesList: IPrintPage[] = [];
  formGroup = new FormGroup({
    Detail: new FormGroup({
      CustomFields: new FormGroup({}),
      ClientKey: new FormControl<string>('', [Validators.required, Validators.minLength(1), Validators.maxLength(200),]),
      Id: new FormControl<string>({ value: '', disabled: true }, { nonNullable: true }),
      StartDate: new FormControl<Date | string | null>(null, {
        validators: [Validators.required],
        updateOn: 'change',
      }),
      EndDate: new FormControl<Date | string | null>(null, {
        validators: [Validators.required],
        updateOn: 'change',
      }),
      Name: new FormControl<string>('', [Validators.required, Validators.minLength(1), Validators.maxLength(100),]),
      Headline: new FormControl<string>(''),
      BodyCopy: new FormControl<string>(''),
      Disclaimer: new FormControl<string>(''),
      ClientComment: new FormControl<string>(''),
      AndUp: new FormControl<string>(''),
      TargetURL: new FormControl<string>('', [Validators.minLength(1), Validators.maxLength(5000),]),
      Rank: new FormControl<number>(1, [Validators.min(1), Validators.max(9999999),]),
      RewardAmount: new FormControl<number>(0, [Validators.min(0), Validators.max(9999),]),
      RewardQuantity: new FormControl<number>(0, [Validators.min(0), Validators.max(9999),]),
      PageNumber: new FormControl<string>('', [Validators.pattern("[a-zA-Z0-9]*")]),
      Position: new FormControl<number | null>(null, [Validators.min(1), Validators.max(9999)]),
      PrintPageId: new FormControl<string | null>(null),
      RewardSpend: new FormControl<number>(0, [Validators.min(0), Validators.max(9999),]),
      Country: new FormControl<string | null>(null),
      Currency: new FormControl<string | null>(null),
      Language: new FormControl<string | null>(null),
      AdditionalDiscountAmount: new FormControl<number>(0, [Validators.min(0), Validators.max(9999),]),
      AdditionalDiscountQuantity: new FormControl<number>(0, [Validators.min(0), Validators.max(9999),]),
      AdditionalDiscountAmount2: new FormControl<number>(0, [Validators.min(0), Validators.max(9999),]),
      AdditionalDiscountQuantity2: new FormControl<number>(0, [Validators.min(0), Validators.max(9999),]),
      AdditionalDiscountAmount3: new FormControl<number>(0, [Validators.min(0), Validators.max(9999),]),
      AdditionalDiscountQuantity3: new FormControl<number>(0, [Validators.min(0), Validators.max(9999),]),
      LoyaltyIndicator: new FormControl<string | null>(null),
      OfferVariantName: new FormControl<string | null>(null),
      BadgeAssetId: new FormControl<string | null>(null),
      OfferTypeId: new FormControl<string | null>(null),
      Status: new FormControl<string>('PENDING'),
      ProductCategoryIds: new FormControl<any[]>([]),
      ParentOfferGroupId: new FormControl<string | null>(null),
      OfferGroupAssociatedRank: new FormControl<number | null>(null),
    })
  }, { updateOn: 'blur' });
  offerId: string = '0';
  eventId!: string;
  isRun = false;
  currentJAICreativeDetail: any;
  isSucceded = false;
  jaiCreativeAdded = false;
  maxDate: Date = new Date(9999, 0, 1);
  offerResponse: OfferDomain<Offer> = {} as OfferDomain<Offer>;
  readOnlyMode: any = null;
  routeQueryParams: any = {};
  isCommentDialogOpened: boolean = false;
  montageBuilderCheckConfigureStatus: boolean = false;
  productCategories$: any[] = [] as any[];
  filteredCategory!: Observable<any[]>;
  categoryCtrl = new FormControl('');
  offerServiceSubs = new Subscription();
  private selectedItems: any = [];
  PreviewMode = PreviewMode;
  public adminSettingStatus: boolean = false;
  hasSaved = false;
  dateFieldsInCustomFields: string[] = [];
  selectFieldsInCustomFields: string[] = [];

  private undoEnabled = false;
  private readonly editUndo: EditUndoController;
  private pendingPricingInit = false;
  private pendingCustomFieldsInit = false;
  private pendingUiInit = false;
  private deferCaptureUntilPricingSync = false;
  private lastKnownOfferTypeId: string | null = null;

  get canUndo(): boolean {
    return this.undoEnabled && this.editUndo.hasUndoActions && this.canEdit();
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public offerService: OfferService<T, TOfferDomain>,
    private offerpromoService: OfferPromoService<TOfferPromo, TPromoDomain>,
    private eventService: EventService<TEvent>,
    private organizationService: OrganizationService,
    private matSnackBar: MatSnackBar,
    private montageBuilderService: MontageBuilderService,
    public utilityService: UtilityService,
    private dialog: MatDialog,
    private authorizationService: AuthorizeControlService,
    private cdRef: ChangeDetectorRef,
    private categoryService: ProductCategoryService<TProductCategory>,
    private settingService: SettingsService,
    private offerDetailService: OfferDetailService,
    private validatorsService: ValidatorsService,
    private customFieldService: CustomFieldService
  ) {
    this.editUndo = new EditUndoController({
      getSnapshot: () => this.buildUndoSnapshot(),
      applyChange: (path, value) => this.applyUndoChange(path, value),
      setCanUndo: (canUndo) => this.offerpromoService.setOfferEditCanUndo(canUndo),
      canUndo: () => this.canUndo,
      sortChangesForApply: sortOfferEditUndoChanges,
    });
  }

  ngOnInit(): void {
    this.offerpromoService.setOfferEditCanUndo(false);
    this.offerpromoService.commentDialogBoxStatus$.subscribe((status: boolean) => { this.isCommentDialogOpened = status });
    this.offerpromoService.eventId = this.route.snapshot.params['eventId'];
    this.loadCustomFields();

    this.offerServiceSubs.add(
      this.offerpromoService.offerEditUndoEnabled$.subscribe((enabled) => {
        this.undoEnabled = !!enabled;
        this.editUndo.setEnabled(this.undoEnabled);
      })
    );
    this.offerServiceSubs.add(
      this.offerpromoService.offerEditUndoRequest$.subscribe(() => {
        this.undoLastAction();
      })
    );

    this.route.params.subscribe((params) => {
      this.offerId = params['offerId'];
      this.eventId = params['eventId'];
      this.getPrintPageDetailsByEvent();
      this.montageBuilderService.montageBuilderStatusBehaviorSubject.next(false);
      this.montageBuilderService.montageBuilderResponseBehaviorSubject.next({});

      this.offerService.getAssets().subscribe((res: any) => {
        this.assetsforBadge = res.value;
      });

      this.offerService.getOfferTypes().subscribe((res: any) => {
        this.offertypes = res.value;
      });

      this.categoryService.getAllCategories().subscribe({
        next: (data: any) => {
          this.productCategories$ = data.value;
          this.categoryCtrl.patchValue('');
          this.getDataById();
        }
      });

      this.offerServiceSubs.add(this.offerService.saveorCancel$.pipe(throttleTime(100)).subscribe((res) => {
        if (res === '1') {
          this.save();
        } else if (res === '0') {
          this.cancel();
        }
      }));
    });

    forkJoin({
      countries: this.offerService.getCountries().pipe(catchError(() => of({ value: [] }))),
      currencies: this.offerService.getCurrencies().pipe(catchError(() => of({ value: [] }))),
      languages: this.offerService.getLanguages().pipe(catchError(() => of({ value: [] })))
    }).subscribe(({ countries, currencies, languages }: any) => {
      this.countries = countries.value;
      this.currencies = currencies.value;
      this.languages = languages.value;
    });

    this.readOnlyMode = this.offerpromoService.getOfferDetailsReadOnlyValue();
    this.offerpromoService.offerDetailsReadOnlyMode$.subscribe((res) => {
      this.readOnlyMode = res;
    });
    this.filteredCategory = this.categoryCtrl.valueChanges.pipe(
      startWith(''),
      map((category) =>
        category ? this.filterCategory(category) : this.productCategories$.slice()
      )
    );
    this.offerpromoService.workflowStages$.subscribe(stages => {
      this.workflowStages = stages;
    });
    this.formGroup.valueChanges.subscribe(() => {
      if (this.formGroup.valid && this.isPricingFormValid) {
        this.offerpromoService.setOfferDetailsValidState = true;
      } else {
        this.offerpromoService.setOfferDetailsValidState = false;
      }
    });

    this.offerServiceSubs.add(
      this.formGroup.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (!this.undoEnabled) return;
          if (this.editUndo.isApplyingUndo) return;
          if (this.editUndo.suppressCapture) return;
          if (this.pendingUiInit) return;
          if (this.pendingPricingInit) return;
          if (this.pendingCustomFieldsInit) return;
          if (this.deferCaptureUntilPricingSync) return;
          this.editUndo.captureIfNeeded();
        })
    );

    this.offerServiceSubs.add(this.formGroup.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter(() => this.formGroup.valid),
        map(() => this.buildOfferPayload()),
        switchMap(offer => this.offerService.getOfferGroupPreviewHtml(offer)),
        takeUntil(this.destroy$)
      ).subscribe((res: any) => {
        this.canEdit() && res?.PricingPreviewHtml && this.offerService.setPreviewHtml(res.PricingPreviewHtml);
      }));
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent) {
    if (this.formGroup.dirty && !this.hasSaved && this.canEdit()) {
      $event.preventDefault();
      $event.returnValue = true;
    }
  }

  canExit(
    route: ActivatedRouteSnapshot,
    currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    if (this.formGroup.dirty && !this.hasSaved && !this.canEdit()) {
      this.formGroup.markAsPristine();
      this.formGroup.markAsUntouched();
      return true;
    }

    if (this.formGroup.dirty && !this.hasSaved) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Close without saving?',
          message: 'You are attempting to navigate out of the Offergroup details tab with unsaved changes.\nHow would you like to proceed?',
          saveText: 'Save and Leave',
          confirmText: 'Leave Without Saving',
          cancelText: 'Stay',
        },
        disableClose: true,
      });

      return dialogRef.afterClosed().pipe(
        switchMap((result) => {
          if (result === true) {
            this.formGroup.markAsPristine();
            this.formGroup.markAsUntouched();
            this.hasSaved = false;
            return of(true);
          }
          if (result === 'save') {
            this.savingForExit = true;
            this.save();
            return this.exitSaveResult$.asObservable().pipe(take(1));
          }

          return of(false);
        })
      );
    }

    return true;
  }

  private resolveExitSave(success: boolean): boolean {
    if (!this.savingForExit) return false;
    this.savingForExit = false;
    this.exitSaveResult$.next(success);
    return true;
  }

  clearData() {
    this.categoryCtrl.patchValue('');
  }

  getAdminSettingInfo() {
    this.settingService.getIntegrationSearch('aiautomation', 'offer').subscribe(res => {
      if (res) {
        this.adminSettingStatus = res.Status;
      }
    })
  }

  filterCategory(name: string) {
    let arr = this.productCategories$.filter(
      (category) => category.ProductCategoryName.toLowerCase().indexOf(name.toLowerCase()) === 0
    );

    return arr.length ? arr : [];
  }

  onCategoryRemoved(category: string) {
    const categories = this.formGroup.controls.Detail.controls.ProductCategoryIds.value as string[];
    this.removeFirst(categories, category);
    this.formGroup.controls.Detail.controls.ProductCategoryIds.setValue(categories); // To trigger change detection
  }

  private removeFirst<T>(array: T[], toRemove: T): void {
    const index = array.indexOf(toRemove);
    if (index !== -1) {
      array.splice(index, 1);
    }
  }

  ngAfterContentChecked() {
    this.cdRef.detectChanges();
  }

  onRun() {
    this.searchCount = 0;
    this.offerpromoService.createJAICreative(this.offerId).subscribe((res: any) => {
      if (res) {
        this.getJAICreative(res.Id);
        this.showJunctionAi = false;
      }
    });
  }

  getJAICreative(id: string) {
    this.jaiCreativeAdded = false;
    if (this.searchCount > 5) {
      return;
    }
    this.searchCount++;
    this.offerpromoService.getJAICreative(id).subscribe((creativeDetails: any) => {
      if (creativeDetails && (creativeDetails.BodyCopy || creativeDetails.Headline || this.searchCount == 5)) {
        this.updateDetails(creativeDetails, true);
      } else {
        setTimeout(() => {
          if (this.searchCount <= 5) {
            this.getJAICreative(id);
          }
        }, 5000);
      }
    }, err => {
      if (this.searchCount <= 5) {
        this.getJAICreative(id);
      }
    });
  }

  updateDetails(creativeDetails: any, isRun: boolean) {
    this.prevHeadline = creativeDetails.Headline;
    this.prevOfferBodyCopy = creativeDetails.BodyCopy;
    this.editUndo.runSuppressed(() => {
      this.formGroup.controls.Detail.controls.Headline.patchValue(creativeDetails.Headline, {
        emitEvent: false,
      });
      this.formGroup.controls.Detail.controls.BodyCopy.patchValue(creativeDetails.BodyCopy, {
        emitEvent: false,
      });
    });
    this.currentJAICreativeDetail = creativeDetails;
    this.isRun = isRun;
    if (creativeDetails.status != 'Completed') {
      this.showJunctionAi = false;
    }
  }

  // navigateChildOffers() {
  //   this.router.navigate([`../`, `childversions`], {
  //     relativeTo: this.route,
  //     queryParamsHandling: 'preserve'
  //   });
  // }

  getDataById() {
    if (this.offerId && this.offerId !== '0') {
      this.offer$ = this.offerDetailService.offer$.pipe(distinctUntilChanged(),
        tap((offer: any) => {
          this.offerTagIds = [];
          this.updatedOfferTagIds = [];
           if (offer && this.offerId === offer?.Detail?.Id) {
            if (this.canEdit() && offer.JAICreativeId) {
              this.searchCount = 0;
              this.getJAICreative(offer.JAICreativeId);
            } else {
              this.showJunctionAi = offer.OfferHasProducts;
            }
            let responseOfferDetails = JSON.parse(JSON.stringify(offer));
            this.montageBuilderService.selectedOfferDetailsBehaviorSubject.next(offer);
            if (responseOfferDetails && responseOfferDetails.ImageEditorConfigId) {
              this.montageBuilderService.montageBuilderCheckConfigureBehaviorSubject.next(true);
              this.montageBuilderService.montageBuilderResponseBehaviorSubject.next(offer);
            } else {
              this.montageBuilderService.montageBuilderCheckConfigureBehaviorSubject.next(false);
              this.montageBuilderService.montageBuilderResponseBehaviorSubject.next(offer);
            }


            this.offerServiceSubs.add(this.montageBuilderService.montageBuilderCheckConfigure$.pipe(take(1)).subscribe((data) => {
              this.montageBuilderCheckConfigureStatus = data;
            }));

            this.offerService.currentRecord = JSON.parse(JSON.stringify(offer));
            if (offer && offer.Detail && offer.Detail.StartDate) {
              offer.Detail.StartDate = new Date(offer.Detail.StartDate);
            }
            if (offer && offer.Detail && offer.Detail.EndDate) {
              offer.Detail.EndDate = new Date(offer.Detail.EndDate);
            }
            const newVersionName = this.offerService.getNewVersion;
            if (newVersionName) {
              offer.Detail.ClientKey = '';
              offer.Detail.OfferVariantName = newVersionName;
            }
            this.customFieldValues = offer.Detail?.CustomFields || {};
            this.initUndoBaselineFromCurrentState();
            this.loadCustomFields();
            this.formGroup.patchValue(offer, { emitEvent: false });
            this.setNulls();
            this.formGroup.controls.Detail.controls.BadgeAssetId.patchValue(offer?.BadgeAssetId as string, { emitEvent: false });
            this.formGroup.controls.Detail.controls.OfferTypeId.patchValue(offer?.OfferTypeId as string, { emitEvent: false });
            this.formGroup.controls.Detail.controls.PrintPageId.patchValue(offer?.PrintPageId as string, { emitEvent: false });
            this.formGroup.controls.Detail.controls.PageNumber.patchValue(offer?.PageNumber as string, { emitEvent: false });
            this.formGroup.controls.Detail.controls.Position.patchValue(offer?.Position as number, { emitEvent: false });
            if (offer.Position == -1) {
              this.formGroup.controls.Detail.controls.Position.patchValue(null, { emitEvent: false });
            }

            this.formGroup.markAllAsTouched();
            if (offer.ParentOfferGroupId == undefined) {
              offer.ParentOfferGroupId = null;
            }

            if (offer.OfferGroupAssociatedRank == undefined) {
              offer.OfferGroupAssociatedRank = -1;
            }
            this.formGroup.controls.Detail.controls.ParentOfferGroupId.patchValue(offer?.ParentOfferGroupId, { emitEvent: false });
            this.formGroup.controls.Detail.controls.OfferGroupAssociatedRank.patchValue(offer?.OfferGroupAssociatedRank, { emitEvent: false });
            this.offerResponse = offer;

            if (offer.OfferTagIds && offer.OfferTagIds.length) {
              this.offerTagIds = offer.OfferTagIds.filter((id: string): id is string => id !== null);
              this.updatedOfferTagIds = offer.OfferTagIds.filter((id: string): id is string => id !== null);
            } else {
              this.offerTagIds = [];
              this.updatedOfferTagIds = [];
            }
            if (offer.ProductCategoryIds && offer.ProductCategoryIds.length > 0) {
              let categories = this.productCategories$.filter((x: any) => offer.ProductCategoryIds?.includes(x.Id));
              this.formGroup.controls.Detail.controls.ProductCategoryIds.patchValue(categories, { emitEvent: false });
              this.selectedItems = categories;
            } else {
              this.formGroup.controls.Detail.controls.ProductCategoryIds.patchValue([], { emitEvent: false });
              this.selectedItems = [];
            }
          }
        })
      );
    } else {
      this.eventService.getEventDomain(this.eventId).subscribe((res) => {
        this.formGroup.controls.Detail.controls.StartDate.patchValue(
          new Date(res?.Detail.StartDate as any)
        );
        this.formGroup.controls.Detail.controls.EndDate.patchValue(
          new Date(res?.Detail.EndDate as any)
        );
      });
    }
  }

  private loadCustomFields(): void {
    this.customFields$ = this.customFieldService.getCustomFieldDefinitionsByVertexType('OFFERGROUP').pipe(
      tap((fields: CustomFieldDefinition[]) => {
        const finalizeAfterCustomFieldsLoaded = this.pendingCustomFieldsInit;
        this.editUndo.runSuppressed(() => {
          const customFieldsGroup = this.formGroup.controls.Detail.controls.CustomFields as FormGroup;

          fields.forEach((field: CustomFieldDefinition) => {
            if (!customFieldsGroup.get(field.FieldName)) {
              const formControl = new FormControl<any>({ value: null, disabled: field.IsReadOnly });
              if (field.FieldType === CustomFieldType.Toggle || field.FieldType === CustomFieldType.Checkbox) {
                formControl.setValue(false, { emitEvent: false });
              }
              customFieldsGroup.addControl(field.FieldName, formControl);
              if (field.FieldType === CustomFieldType.Date) {
                this.dateFieldsInCustomFields.push(field.FieldName);
              }
              if (field.FieldType === CustomFieldType.MultiSelect) {
                this.selectFieldsInCustomFields.push(field.FieldName);
              }
            }
          });
          if (Object.keys(this.customFieldValues).length > 0) {
            Object.keys(this.customFieldValues).forEach(key => {
              if (customFieldsGroup.get(key)) {
                if (this.dateFieldsInCustomFields.includes(key) && this.customFieldValues[key]) {
                  customFieldsGroup.get(key)?.setValue(new Date(this.customFieldValues[key]), {
                    emitEvent: false,
                  });
                } else if (this.selectFieldsInCustomFields.includes(key) && this.customFieldValues[key]) {
                  customFieldsGroup.get(key)?.setValue(
                    typeof this.customFieldValues[key] === 'string'
                      ? [this.customFieldValues[key]]
                      : this.customFieldValues[key],
                    { emitEvent: false }
                  );
                } else {
                  customFieldsGroup.get(key)?.setValue(this.customFieldValues[key], { emitEvent: false });
                }
              }
            });
          }
        });

        if (finalizeAfterCustomFieldsLoaded) {
          this.pendingCustomFieldsInit = false;
          this.tryFinalizeUndoBaseline();
        }
      })
    );
  }

  getCustomFieldsGroup(): FormGroup {
    return this.formGroup.controls.Detail.controls.CustomFields as FormGroup;
  }

  validateDate(): boolean {
    let startDate = this.formGroup.controls.Detail.controls.StartDate;
    let endDate = this.formGroup.controls.Detail.controls.EndDate;
    if (endDate && endDate.value && startDate && startDate.value) {
      if (endDate.value < startDate.value) {
        this.matSnackBar.open(
          'End Date must be equal to or greater than Start Date',
          'Error',
          { verticalPosition: 'top', panelClass: ['snackbar-error'] }
        );
        return false;
      }
    }
    return true;
  }

  clearNulls(offer: OfferDomain<T>): OfferDomain<T> {
    const properties = [
      'RewardAmount',
      'RewardQuantity',
      'RewardSpend',
      'AdditionalDiscountAmount',
      'AdditionalDiscountQuantity',
      'AdditionalDiscountAmount2',
      'AdditionalDiscountQuantity2',
      'AdditionalDiscountAmount3',
      'AdditionalDiscountQuantity3',
      'Position',
    ] as const;

    properties.forEach((property) => {
      if (offer.Detail[property] == null) {
        offer.Detail[property] = -1;
      }
    });

    return offer;
  }

  setNulls(): void {
    const controlNames = [
      'RewardAmount',
      'RewardQuantity',
      'RewardSpend',
      'AdditionalDiscountAmount',
      'AdditionalDiscountQuantity',
      'AdditionalDiscountAmount2',
      'AdditionalDiscountQuantity2',
      'AdditionalDiscountAmount3',
      'AdditionalDiscountQuantity3',
      'Position',
      'Country',
      'Currency',
      'Language',
    ];

    controlNames.forEach((controlName) => {
      const control = this.formGroup.controls.Detail.get(controlName) as FormControl;
      if (control?.getRawValue() === -1) {
        control.setValue(null, { emitEvent: false });
      }
    });
  }

  trimControlValues(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key: string) => {
      const abstractControl = formGroup.get(key);
      if (abstractControl instanceof FormGroup) {
        this.trimControlValues(abstractControl);
      } else {
        if (typeof abstractControl?.value == 'string') {
          abstractControl.setValue(abstractControl?.value.trim());
        }
      }
    });
  }

  formatDate(date: Date | string | null) {
    if (date) {
      const newDate = dayjs(date).format('MM/DD/YYYY');
      return newDate;
    } else {
      return '';
    }
  }

  onPricingFormValueChange($event: any) {
    this.pricingDetails = $event;

    if (this.canEdit()) {
      let offerPayload = this.buildOfferPayload();
      this.offerService.getOfferGroupPreviewHtml(offerPayload).subscribe((res: any) => {
        this.offerService.setPreviewHtml(res.PricingPreviewHtml);
      });
    }

    if (this.pendingPricingInit) {
      this.pendingPricingInit = false;
      this.tryFinalizeUndoBaseline();
      return;
    }

    if (this.deferCaptureUntilPricingSync) {
      this.deferCaptureUntilPricingSync = false;
      if (this.canCaptureUndoForUserActions()) {
        this.editUndo.captureIfNeeded();
        this.syncLastKnownOfferTypeId();
      }
      return;
    }

    if (this.undoEnabled && !this.editUndo.isApplyingUndo && !this.editUndo.suppressCapture) {
      this.editUndo.captureIfNeeded();
    }
  }

  onOfferTypeSelectionChange(event: MatSelectChange) {
    if (this.editUndo.isApplyingUndo || this.editUndo.suppressCapture) return;
    if (this.pendingPricingInit || this.pendingCustomFieldsInit || this.pendingUiInit) {
      return;
    }
    const nextId = (event.value as string | null) ?? null;
    if (nextId === this.lastKnownOfferTypeId) return;
    this.deferCaptureUntilPricingSync = true;
  }

  onOfferTypePanelClosed() {
    if (!this.deferCaptureUntilPricingSync) return;
    this.deferCaptureUntilPricingSync = false;
    if (this.canCaptureUndoForUserActions()) {
      this.editUndo.captureIfNeeded();
      this.syncLastKnownOfferTypeId();
    }
  }

  onPricingFormValidityChange($event: boolean) {
    this.isPricingFormValid = $event;

    if (this.formGroup.valid && this.isPricingFormValid) {
      this.offerpromoService.setOfferDetailsValidState = true;
    } else {
      this.offerpromoService.setOfferDetailsValidState = false;
    }
  }

  getSelectedOfferTypeId(): any {
    const offerTypeId = this.formGroup.controls.Detail.controls.OfferTypeId.value;
    return offerTypeId;
  }

  removeDetailFields(offer: OfferDomain<T>, fields: string[]) {
    fields.forEach(field => delete (offer.Detail as any)[field]);
  }

  buildOfferPayload(): OfferDomain<T> {
    let offer = this.formGroup.getRawValue() as unknown as OfferDomain<T>;

    const detailForm = this.formGroup.controls.Detail.controls;

    // Dates
    offer.Detail.StartDate = this.formatDate(detailForm.StartDate.value);
    offer.Detail.EndDate = this.formatDate(detailForm.EndDate.value);

    // Cleanup
    offer = this.clearNulls(offer);

    // Merge pricing + locale
    offer.Detail = {
      ...offer.Detail,
      ...this.pricingDetails,
      Country: detailForm.Country.value || null,
      Currency: detailForm.Currency.value || null,
      Language: detailForm.Language.value || null
    };

    // Tags
    offer.OfferTagIds = this.updatedOfferTagIds;

    // Product Categories
    const categories = detailForm.ProductCategoryIds.value;
    offer.ProductCategoryIds = categories?.map((cat: any) => cat.Id) ?? [];

    // Versions
    offer.VersionIds = this.offerService.selectedVersionsIds;

    // Badge
    offer.BadgeAssetId = detailForm.BadgeAssetId.value as string;
    delete offer.Detail.BadgeAssetId;

    // Offer Type
    offer.OfferTypeId = detailForm.OfferTypeId.value as string;
    delete offer.Detail.OfferTypeId;
    offer.OfferTypeName =
      this.offertypes?.find((x: any) => x.Id === offer.OfferTypeId)?.OfferTypeName || '';

    // Pagination / Positioning
    offer.PageNumber = detailForm.PageNumber.value as string;
    offer.Position = detailForm.Position.value as number;
    offer.PrintPageId = detailForm.PrintPageId.value as string;

    // Offer Group
    offer.ParentOfferGroupId = detailForm.ParentOfferGroupId.value as string;
    offer.OfferGroupAssociatedRank = detailForm.OfferGroupAssociatedRank.value as number;

    // Remove duplicated fields from Detail
    this.removeDetailFields(offer, ['PageNumber', 'Position', 'PrintPageId']);

    return offer;
  }

  save() {
    if (!this.canEdit()) {
      this.resolveExitSave(true);
      return;
    }
    this.clearUndoStack();
    (document.activeElement as HTMLElement)?.blur();
    this.pricingForm?.commitPendingEdits();

    this.trimControlValues(this.formGroup);

    this.formGroup.markAsDirty();
    this.formGroup.markAllAsTouched();
    if (this.formGroup.valid && this.isPricingFormValid) {
      if (!this.validateDate()) {
        this.resolveExitSave(false);
        return;
      }

      let offer = this.buildOfferPayload();
      const customFields = Object.keys(this.formGroup.controls.Detail.controls.CustomFields.controls);
      if (customFields && customFields.length > 0) {
        customFields.forEach((fieldName) => {
          if(this.dateFieldsInCustomFields.includes(fieldName) && this.formGroup.controls.Detail.controls.CustomFields.get(fieldName)?.value) {
            offer.Detail.CustomFields[fieldName] = this.formatDate(this.formGroup.controls.Detail.controls.CustomFields.get(fieldName)?.value);
          }
        });
      }

      const objecttoPass = this.offerId !== '0' ? this.getUpdatedRecordInfo(offer) : offer;
      const offers = [this.offerId];

      let apiToCall;
      if (objecttoPass.Properties && Object.keys(objecttoPass.Properties).length === 0 && objecttoPass.Properties.constructor === Object) {
        delete objecttoPass.Properties;
      }
      this.getUpdatedPrintDetails(objecttoPass);
      apiToCall = this.offerService.saveOfferGroupProperties(offers, objecttoPass);

      apiToCall.subscribe({
        next: (response: any) => {
          this.hasSaved = true;
          this.formGroup.markAsPristine();
          this.offerService.newVersion = '';
          if (this.savingForExit) {
            if (response?.Id) {
              this.offerId = response.Id;
            }
            this.offerService.reload();
            this.offerDetailService.reload();
            this.resolveExitSave(true);
            return;
          }
          if (response) {
            const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
              data: {
                title: 'Saved Successfully',
                confirmText: 'Continue Editing',
                cancelText: 'Back to Offers',
              },
              disableClose: true,
            });
            confirmDialog.afterClosed().subscribe((confirmResult) => {
              if (confirmResult) {
                this.routeQueryParams = this.route.snapshot.queryParams;
                this.offerId = response.Id;
                this.offerService.reload();
                this.offerDetailService.reload();

                const currentUrlTree = this.router.createUrlTree(
                  [`../../../${response.Id}/offergroups/offergrpdetails`],
                  {
                    relativeTo: this.route,
                    queryParams: this.routeQueryParams,
                    queryParamsHandling: 'merge',
                  }
                );

                this.router.navigateByUrl(currentUrlTree, { skipLocationChange: false }).then(() => {
                  this.formGroup.markAsPristine();
                  this.formGroup.markAsUntouched();
                  this.offerService.reload();
                  this.getPrintPageDetailsByEvent();
                });
              } else {
                this.offerService.reload();
                this.router.navigate([`../../../`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
              }
            });
          }
        },
        error: (error) => {
          this.resolveExitSave(false);
          if (error.status === 500) {
            log.error('500 Error saving offer', error);
            this.matSnackBar.open('500 Error saving offer', 'Error', {
              verticalPosition: 'top',
              panelClass: ['snackbar-error'],
            });
          }
          if (error.status === 400) {
            const apiValidations: any = error.error;
            if (Array.isArray(apiValidations)) {
              apiValidations.forEach((validation: any) => {
                if (validation.PropertyName != null && validation.PropertyName != undefined) {
                  validation.PropertyName = validation.PropertyName.replace('OfferDomain.Detail.', '');
                }
                if (this.formGroup?.controls.Detail.get(validation.PropertyName)) {
                  const control = this.formGroup?.controls.Detail.get(validation.PropertyName);
                  if (control) {
                    control.markAsTouched();
                    control.setErrors({ invalid: validation.ErrorMessage });
                    this.matSnackBar.open(validation.ErrorMessage, 'Error', {
                      verticalPosition: 'top',
                      panelClass: ['snackbar-error'],
                    });
                  }
                } else {
                  this.matSnackBar.open(error.error, 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'], });
                }
              });
            } else {
              this.matSnackBar.open(typeof apiValidations == 'object' && apiValidations.Message ? apiValidations.Message : apiValidations, 'Error', { verticalPosition: 'top', panelClass: ['snackbar-error'], });
            }
          }
        },
      });
    } else if (this.savingForExit) {
      this.matSnackBar.open('Please fix validation errors before saving.', 'Error',{ verticalPosition: 'top', panelClass: ['snackbar-error'] });
      this.resolveExitSave(false);
    }
    this.offerService.setSaveorCancel('');
  }

  openMontageEditor() {
    this.montageBuilderService.montageBuilderStatusBehaviorSubject.next(true);
    this.router.navigate([`../`, `montage-editor`], { relativeTo: this.route, queryParamsHandling: 'merge' });
  }

  getUpdatedPrintDetails(objecttoPass: any) {
    if (objecttoPass.PrintPageId || objecttoPass.PrintPageId == "" || objecttoPass.Position || objecttoPass.Position == null) {
      objecttoPass['PrintPageId'] = this.formGroup.controls.Detail.controls.PrintPageId
        .value as string;
      this.formGroup.controls.Detail.controls.Position
        .value as number == null ? objecttoPass['Position'] = -1 : objecttoPass['Position'] = this.formGroup.controls.Detail.controls.Position
          .value as number;
    }
  }

  openDialog($event: any, offer: any) {
    if (this.isCommentDialogOpened == false) {
      let commentElementStyle = document.getElementById(offer.DomainId) as HTMLElement;
      const dialogConfig = new MatDialogConfig();
      dialogConfig.hasBackdrop = true;
      dialogConfig.disableClose = false;
      dialogConfig.data = {
        headline: offer.Detail.Headline,
        channel: 'OFFERGROUP',
        parentId: offer.Detail.Id,
        clientKey: offer.Detail.ClientKey,
        bodyCopy: offer.Detail.BodyCopy,
        workflowStage: this.workflowStages.length ? this.workflowStages[0] : null,
      };
      let confirmDialog = this.dialog.open(CommentsComponent, dialogConfig);
      confirmDialog.afterOpened().subscribe(() => {
        if (commentElementStyle) {
          commentElementStyle.style.color = "blue";
          this.offerpromoService.commentDialogBoxStatusBehaviorSubject.next(true);
        }
      });
      confirmDialog.afterClosed().subscribe((data) => {
        if (data > 0) {
          Object(offer).CommentsCount = data;
        }
        if (commentElementStyle) {
          commentElementStyle.style.color = "black";
          this.offerpromoService.commentDialogBoxStatusBehaviorSubject.next(false);
        }
      });
    }
  }

  cancel(): void {
    if (this.formGroup.dirty && this.canEdit() == true) {
      const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
        data: {
          title: 'Close without saving?',
          message: 'This offer has unsaved changes',
          confirmText: 'Continue Editing',
          cancelText: 'Close',
        },
        disableClose: true,
      });
      confirmDialog.afterClosed().subscribe((confirmResult) => {
        if (!confirmResult) {
          this.clearUndoStack();
          this.formGroup.reset();
          this.router.navigate([`../../../`], {
            relativeTo: this.route,
            queryParamsHandling: 'preserve'
          });
        }
      });
    } else {
      this.clearUndoStack();
      this.formGroup.reset();
      this.router.navigate([`../../../`], { relativeTo: this.route, queryParamsHandling: 'preserve' });
    }
  }

  updateTableStatus(status: Status) {
    let selectedRecordsDetails = [this.offerResponse];
    this.updateRecordsStatus(selectedRecordsDetails, status);
  }

  updateRecordsStatus(selectedRecordsDetails: Array<any>, status: any) {
    if (selectedRecordsDetails && selectedRecordsDetails.length > 0) {
      const recordstoupdate = selectedRecordsDetails.map((x: any) => {
        return {
          Id: x.Detail.Id,
          Status: status,
          Label: x.Detail.Label
        };
      });

      this.offerpromoService.updateStatus(recordstoupdate).subscribe({
        next: () => {
          this.getDataById();
        },
      });
    }
  }

  deleteRecord() {
    if (this.canEdit() == false) {
      return;
    }
    const record = this.formGroup.getRawValue();
    const variantName = record.Detail?.OfferVariantName;
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message: variantName 
          ? `Are you sure you want to delete "${record.Detail.Name} (${variantName})"?`
          : `Are you sure you want to delete ${record.Detail.Name}?`,
      },
      disableClose: true,
    });

    confirmDialog.afterClosed().subscribe((confirmResult) => {
      if (confirmResult) {
        this.offerpromoService.deleteOfferGroup(record.Detail.Id).subscribe({
          next: () => {
            this.matSnackBar.open(
              `${record.Detail.Name} deleted`,
              'OK',
              DEFAULT_SNACKBAR_CONFIG
            );
            this.offerpromoService.reload();
            this.router.navigate([`../../../`], {
              relativeTo: this.route,
              queryParamsHandling: 'preserve'
            });
          },
          error: (error) => {
            log.error('Error deleting offer', error);

            if (error.error.value) {
              throw new Error(error.error.value);
            } else {
              throw new Error(error.message);
            }
          },
        });
      }
    });
  }

  tagSelected(tagId: string): void {
    if (this.updatedOfferTagIds.includes(tagId)) {
      return;
    }
    this.applyUserOfferTagIds([...this.updatedOfferTagIds, tagId]);
  }

  addTag(offerTag: OfferTag): void {
    if (this.updatedOfferTagIds.includes(offerTag.Id)) {
      return;
    }
    this.applyUserOfferTagIds([...this.updatedOfferTagIds, offerTag.Id]);
    this.matSnackBar.open(`${offerTag.OfferTagName} saved`, 'OK', DEFAULT_SNACKBAR_CONFIG);
  }

  removeTag(tagId: string): void {
    this.applyUserOfferTagIds(this.updatedOfferTagIds.filter(id => id !== tagId));
  }

  public getImageSrc(assetsUrl: any, assets: any) {
    let imagesrc = '';
    if (assets.Type && assets.Type === 'application/pdf') {
      imagesrc = './assets/images/pdf.png';
    } else if (assets.Type && assets.Type.indexOf('audio') > -1) {
      imagesrc = './assets/images/mp3.jpg';
    } else if (assets.Type && assets.Type.indexOf('video') > -1) {
      imagesrc = './assets/images/mp4.png';
    } else if (assets.Type && assets.Type.indexOf('text') > -1) {
      imagesrc = './assets/images/text.png';
    } else {
      imagesrc = assetsUrl + '/' + assets.FileName;
    }
    return imagesrc;
  }

  canEdit(): boolean {
    const hasAccess = this.authorizationService.checkAccess('edit||offermang') && this.readOnlyMode != true;
    if (hasAccess) {
      this.formGroup.disabled ? this.formGroup.enable() : null;
      this.tagCtrl.enable();
    } else {
      this.formGroup.enabled ? this.formGroup.disable() : null;
      this.tagCtrl.disable();
    }
    return hasAccess;
  }

  getUpdatedRecordInfo(record: any) {
    const allowedNullProps = new Set([
      'Detail.Country',
      'Detail.Currency',
      'Detail.Language'
    ]);
    const objecttoPass: any = {
      Properties: {
        CustomFields: {}
      },
    };
    const currentOfferInfo = this.offerService.getCurrentRecord;
    if (currentOfferInfo) {
      const properties = Object.keys(record);
      for (let index = 0; index <= properties.length - 1; index++) {
        if (
          record[properties[index]] != null &&
          record[properties[index]] != undefined &&
          typeof record[properties[index]] === 'object' &&
          !Array.isArray(record[properties[index]])
        ) {
          const childProperties = Object.keys(record[properties[index]]);
          if (childProperties && childProperties.length > 0) {
            const currentRecord = currentOfferInfo[properties[index]];
            const updatedRecord = record[properties[index]];
            for (let childIndex = 0; childIndex <= childProperties.length - 1; childIndex++) {
              const value = updatedRecord[childProperties[childIndex]];
              const propPath = `${properties[index]}.${childProperties[childIndex]}`;
              const isAllowedNull = allowedNullProps.has(propPath);

              if (currentRecord && currentRecord[childProperties[childIndex]] !== value && (value !== undefined && (value !== null || isAllowedNull))) {
                objecttoPass['Properties'][childProperties[childIndex]] = value;
              } else if (!currentRecord && (value !== undefined && (value !== null || isAllowedNull))) {
                objecttoPass['Properties'][childProperties[childIndex]] = value;
              }
            }
          }
        } else if (
          (!Array.isArray(currentOfferInfo[properties[index]]) &&
            currentOfferInfo[properties[index]] !==
            record[properties[index]]) ||
          (Array.isArray(currentOfferInfo[properties[index]]) &&
            !this.areArraysEqual(
              currentOfferInfo[properties[index]],
              record[properties[index]]
            ))
        ) {
          objecttoPass[properties[index]] = record[properties[index]];
        }
      }
    }
    return objecttoPass;
  }

  areArraysEqual(arr1: string[], arr2: string[]): boolean {
    if (arr1.length !== arr2.length) {
      return false;
    }
    const sortedArr1 = arr1.slice().sort();
    const sortedArr2 = arr2.slice().sort();
    for (let i = 0; i < sortedArr1.length; i++) {
      if (sortedArr1[i] !== sortedArr2[i]) {
        return false;
      }
    }
    return true;
  }

  clearSearch() {
    this.categoryCtrl.patchValue('');
    this.formGroup.controls.Detail.controls.ProductCategoryIds.patchValue(this.selectedItems);
  }

  updateSelectedItems(matSelect: MatSelect, category: any) {
    if (!this.selectedItems) this.selectedItems = [];
    const checkedItems = matSelect.options.filter(x => x.selected).map(i => i.value);
    if (checkedItems && checkedItems.length > 0) {
      if (checkedItems.find(x => x.Id === category.Id)) {
        this.selectedItems.push(category);
      } else {
        this.selectedItems = this.selectedItems.filter((i: any) => i.Id !== category.Id);
      }
    } else {
      this.selectedItems = this.selectedItems.filter((i: any) => i.Id !== category.Id);
    }
  }

  getPrintPageDetailsByEvent() {
    this.offerpromoService.GetAllPrintPagesByEventforOffers(this.eventId).subscribe((res) => {
      this.printPagesList = res;
    })
  }

  clearUndoStack() {
    this.editUndo.clearStack();
  }

  undoLastAction() {
    if (!this.editUndo.undoLast()) {
      return;
    }
    this.deferCaptureUntilPricingSync = false;
    this.syncLastKnownOfferTypeId();
  }

  onDateCommitted() {
    this.captureUndoOnFieldCommit();
  }

  onRankCommitted() {
    this.captureUndoOnFieldCommit();
  }

  private captureUndoOnFieldCommit(): void {
    this.editUndo.captureOnFieldCommit(() => this.canCaptureUndoForUserActions());
  }

  private applyUndoChange(path: string, value: any) {
    if (path.startsWith('pricing.')) {
      const key = path.slice('pricing.'.length);
      const patch: Record<string, any> = { [key]: value === -1 ? null : value };
      this.pricingForm?.applyExternalValuePatch(patch);
      this.pricingDetails = { ...(this.pricingDetails || {}), [key]: value };
      return;
    }

    if (path === 'updatedOfferTagIds') {
      this.applyOfferTagIds(Array.isArray(value) ? value : []);
      return;
    }

    const ctrl = this.formGroup.get(path);
    if (ctrl) {
      ctrl.setValue(value, { emitEvent: false });
      ctrl.updateValueAndValidity({ emitEvent: false });
      ctrl.markAsDirty();
      ctrl.markAsTouched();
      if (path === 'Detail.OfferTypeId') {
        this.cdRef.detectChanges();
      }
    }
  }

  private buildUndoSnapshot(): any {
    return {
      Detail: this.formGroup.getRawValue()?.Detail ?? {},
      pricing: { ...(this.pricingDetails || {}) },
      updatedOfferTagIds: [...(this.updatedOfferTagIds || [])],
    };
  }

  private initUndoBaselineFromCurrentState() {
    this.clearUndoStack();
    if (!this.undoEnabled) {
      this.pendingPricingInit = false;
      this.pendingCustomFieldsInit = false;
      this.editUndo.suppressCapture = false;
      return;
    }
    this.pendingPricingInit = true;
    this.pendingCustomFieldsInit = true;
    this.editUndo.suppressCapture = true;
    this.pendingUiInit = true;
    setTimeout(() => {
      this.pendingUiInit = false;
    });
  }

  private tryFinalizeUndoBaseline(): void {
    if (!this.undoEnabled) return;
    if (this.pendingPricingInit || this.pendingCustomFieldsInit) return;

    this.editUndo.finalizeBaselineFromLoad();
    this.syncLastKnownOfferTypeId();
  }

  private syncLastKnownOfferTypeId(): void {
    this.lastKnownOfferTypeId =
      this.formGroup.controls.Detail.controls.OfferTypeId.value ?? null;
  }

  private applyOfferTagIds(tagIds: string[]): void {
    const next = [...tagIds];
    this.updatedOfferTagIds = next;
    this.offerTagIds = [...next];
  }

  private canCaptureUndoForUserActions(): boolean {
    return (
      this.undoEnabled &&
      !this.editUndo.isApplyingUndo &&
      !this.editUndo.suppressCapture &&
      !this.pendingUiInit &&
      !this.pendingPricingInit &&
      !this.pendingCustomFieldsInit
    );
  }

  private areTagIdListsEqual(a: string[], b: string[]): boolean {
    return a.length === b.length && a.every((id, index) => id === b[index]);
  }

  private applyUserOfferTagIds(nextIds: string[]): void {
    const normalized = [...nextIds];
    if (this.areTagIdListsEqual(this.updatedOfferTagIds, normalized)) {
      return;
    }

    this.editUndo.applyUserMutation(
      () => this.applyOfferTagIds(normalized),
      () => this.canCaptureUndoForUserActions()
    );
  }

  ngOnDestroy() {
    this.clearUndoStack();
    this.dialog.closeAll();
    this.offerServiceSubs.unsubscribe();
  }

  onRankInput(event: any) {
    const filtered = this.validatorsService.allowOnlyDigits(event);
    const numericValue = filtered ? Number(filtered) : null;
    this.formGroup.controls.Detail.controls['Rank']?.setValue(numericValue, { emitEvent: false });
  }
}
